import { useState } from 'react';
import { Stack, Switch, TextInput } from '@mantine/core';
import { MRT_Cell } from 'mantine-react-table';

import { Site } from 'interfaces/site';

type TranslateFn = (
  key: string,
  defaultValue?: string,
  options?: Record<string, unknown>
) => string;

export type SitePaymentToggleKey = keyof Pick<
  Site,
  'etransfer' | 'creditCard' | 'cheque'
>;
export type SitePaymentDetailKey = keyof Pick<
  Site,
  'etransferEmail' | 'etransferPasswordRequirement' | 'creditCardPhoneNumber'
>;

export interface SitePaymentOptionConfig {
  toggleKey: SitePaymentToggleKey;
  label: string;
  description: string;
  toggleTestId: string;
  dependentFields?: Array<{
    key: SitePaymentDetailKey;
    label: string;
    placeholder: string;
    testId: string;
  }>;
}

const getCachedValue = (
  cell: MRT_Cell<Site>,
  key: SitePaymentToggleKey | SitePaymentDetailKey
) => cell.row._valuesCache[key] ?? cell.row.original?.[key];

const setCachedValue = (
  cell: MRT_Cell<Site>,
  key: SitePaymentToggleKey | SitePaymentDetailKey,
  value: Site[typeof key] | null
) => {
  cell.row._valuesCache[key] = value;
};

export const SitePaymentOptionEdit = ({
  cell,
  config,
}: {
  cell: MRT_Cell<Site>;
  config: SitePaymentOptionConfig;
}) => {
  const [enabled, setEnabled] = useState<boolean>(
    Boolean(getCachedValue(cell, config.toggleKey))
  );
  const [fieldValues, setFieldValues] = useState<
    Partial<Record<SitePaymentDetailKey, string>>
  >(() =>
    Object.fromEntries(
      (config.dependentFields ?? []).map((field) => [
        field.key,
        String(getCachedValue(cell, field.key) ?? ''),
      ])
    )
  );

  setCachedValue(cell, config.toggleKey, enabled);

  for (const field of config.dependentFields ?? []) {
    setCachedValue(
      cell,
      field.key,
      enabled ? fieldValues[field.key] || '' : null
    );
  }

  return (
    <Stack gap="xs">
      <Switch
        label={config.label}
        description={config.description}
        checked={enabled}
        data-testid={config.toggleTestId}
        onChange={(e) => {
          const nextEnabled = e.target.checked;
          setEnabled(nextEnabled);
          setCachedValue(cell, config.toggleKey, nextEnabled);
          for (const field of config.dependentFields ?? []) {
            setCachedValue(
              cell,
              field.key,
              nextEnabled ? fieldValues[field.key] || '' : null
            );
          }
        }}
      />
      {enabled
        ? (config.dependentFields ?? []).map((field) => (
            <TextInput
              key={field.key}
              label={field.label}
              placeholder={field.placeholder}
              data-testid={field.testId}
              value={fieldValues[field.key] || ''}
              onChange={(e) => {
                const nextValue = e.target.value;
                setFieldValues((current) => ({
                  ...current,
                  [field.key]: nextValue,
                }));
                setCachedValue(cell, field.key, nextValue);
              }}
            />
          ))
        : null}
    </Stack>
  );
};

export const getETransferPaymentOption = (
  translate: TranslateFn
): SitePaymentOptionConfig => ({
  toggleKey: 'etransfer',
  label: translate('pages:sites.paymentOptions.etransfer.label', 'E-transfer'),
  description: translate(
    'pages:sites.paymentOptions.etransfer.description',
    'Allow e-transfer as a payment option.'
  ),
  toggleTestId: 'etransferToggle',
  dependentFields: [
    {
      key: 'etransferEmail',
      label: translate(
        'pages:sites.paymentOptions.etransfer.fields.email.label',
        'E-transfer email'
      ),
      placeholder: translate(
        'pages:sites.paymentOptions.etransfer.fields.email.placeholder',
        'billing@example.com'
      ),
      testId: 'etransferEmailInput',
    },
    {
      key: 'etransferPasswordRequirement',
      label: translate(
        'pages:sites.paymentOptions.etransfer.fields.passwordRequirement.label',
        'Password requirement'
      ),
      placeholder: translate(
        'pages:sites.paymentOptions.etransfer.fields.passwordRequirement.placeholder',
        'Optional password instructions'
      ),
      testId: 'etransferPasswordRequirementInput',
    },
  ],
});

export const getCreditCardPaymentOption = (
  translate: TranslateFn
): SitePaymentOptionConfig => ({
  toggleKey: 'creditCard',
  label: translate(
    'pages:sites.paymentOptions.creditCard.label',
    'Credit card'
  ),
  description: translate(
    'pages:sites.paymentOptions.creditCard.description',
    'Allow credit card as a payment option.'
  ),
  toggleTestId: 'creditCardToggle',
  dependentFields: [
    {
      key: 'creditCardPhoneNumber',
      label: translate(
        'pages:sites.paymentOptions.creditCard.fields.phoneNumber.label',
        'Credit card phone number'
      ),
      placeholder: translate(
        'pages:sites.paymentOptions.creditCard.fields.phoneNumber.placeholder',
        'Phone number'
      ),
      testId: 'creditCardPhoneNumberInput',
    },
  ],
});

export const getChequePaymentOption = (
  translate: TranslateFn
): SitePaymentOptionConfig => ({
  toggleKey: 'cheque',
  label: translate('pages:sites.paymentOptions.cheque.label', 'Cheque'),
  description: translate(
    'pages:sites.paymentOptions.cheque.description',
    'Allow cheque as a payment option.'
  ),
  toggleTestId: 'chequeToggle',
});
